
export enum StorageCategory {
  TOKEN = 'token',
  SETTINGS = 'settings',
  EXPERT = 'expert',
  STUDENT = "student",
  MEDIA = "media",
}

export enum TokenType {
  Normal = 'normal',
  Refresh = 'refreshToken',
}

export enum MediaKeys {
  DEFAULT_INPUT_VIDEO_DEVICE = 'default_input_video_device',
  DEFAULT_INPUT_AUDIO_DEVICE = 'default_input_audio_device',
  DEFAULT_OUTPUT_AUDIO_DEVICE = 'default_output_audio_device',
  INTPUT_VIDEO_ENABLED = 'intput_video_enabled',
  INTPUT_AUDIO_ENABLED = 'intput_audio_enabled',
  INTPUT_SCREENS_VIDEO_ENABLED = 'intput_screens_video_enabled',
  INTPUT_BOARDS_VIDEO_ENABLED = 'intput_boards_video_enabled',
}

export enum StudentKeys {
  KEY = "key", // TODO
}
export interface StorageKeysMap {
  [StorageCategory.TOKEN]: TokenType,
  [StorageCategory.SETTINGS]: undefined,
  [StorageCategory.EXPERT]: 'home_params',
  [StorageCategory.STUDENT]: StudentKeys,
  [StorageCategory.MEDIA]: MediaKeys,
}

const storage = localStorage;

// ---------------- NATIVE ------------------ //

const generateKey = (category : StorageCategory, key?: string) => (
  key ? `${category}_${key}` : category
);

const setItem = <T extends StorageCategory>(
  category : T,
  key: StorageKeysMap[T],
  value: string,
) => storage.setItem(generateKey(category, key), value);

const getItem = <T extends StorageCategory>(
  category : T,
  key?: StorageKeysMap[T],
) => storage.getItem(generateKey(category, key));

const removeItem = <T extends StorageCategory>(
  category : T,
  key?: StorageKeysMap[T],
) => storage.removeItem(generateKey(category, key));

// ---------------- TOKEN ------------------ //

export const setToken = (
  token?: string,
  tokenType: TokenType = TokenType.Normal,
) => {
  if (token) setItem(StorageCategory.TOKEN, tokenType, token);
  else removeItem(StorageCategory.TOKEN, tokenType);
};

export const getToken = (
  tokenType: TokenType = TokenType.Normal,
) => getItem(StorageCategory.TOKEN, tokenType);

export const removeToken = (
  tokenType: TokenType = TokenType.Normal,
) => removeItem(StorageCategory.TOKEN, tokenType);

// ---------------- REFRESH TOKEN ------------------ //

// ---------------- SETTINGS ------------------ //

export const setSettings = (
  settings: string
) => setItem(StorageCategory.SETTINGS, undefined, settings);

export const getSettings = () => getItem(StorageCategory.SETTINGS);

export const removeSettings = () => removeItem(StorageCategory.SETTINGS);

// ---------------- EXPERT ------------------ //

export interface StorageExpertHomeParams {
  micOffOutsideOfAnEvent?: boolean;
}
export const setExpertHomeParams = (
  params: StorageExpertHomeParams
) => setItem(StorageCategory.EXPERT, "home_params", JSON.stringify(params));

export const getExpertHomeParams = (): StorageExpertHomeParams | undefined => {
  const str = getItem(StorageCategory.EXPERT, "home_params");
  let result: StorageExpertHomeParams | undefined;
  if (!str) return undefined;
  try {
    result = JSON.parse(str);
  } catch(err) {};
  return result;
};

export const removeExpertHomeParams = () => removeItem(StorageCategory.EXPERT, "home_params");

// ---------------- STUDENT ------------------ //

export interface StudentData {
  [StudentKeys.KEY]: string;
}

export const setStudentParam = <T extends StudentKeys>(
  key: T,
  data: StudentData[T]
) => setItem(StorageCategory.STUDENT, key, JSON.stringify(data));

export const getStudentParam = <T extends StudentKeys>(key: T): StudentData[T] | null => {
  const str = getItem(StorageCategory.STUDENT, key);
  if (!str) return null;
  let result = null;
  try {
    result = JSON.parse(str);
  } catch(err) {}
  return result;
};

export const removeStudentParam = <T extends StudentKeys>(key: T) => removeItem(StorageCategory.STUDENT, key);

// ---------------- Media ------------------ //

export interface MediaData {
  [MediaKeys.DEFAULT_INPUT_VIDEO_DEVICE]: string;
  [MediaKeys.DEFAULT_INPUT_AUDIO_DEVICE]: string;
  [MediaKeys.DEFAULT_OUTPUT_AUDIO_DEVICE]: string;
  [MediaKeys.INTPUT_VIDEO_ENABLED]: boolean;
  [MediaKeys.INTPUT_AUDIO_ENABLED]: boolean;
  [MediaKeys.INTPUT_SCREENS_VIDEO_ENABLED]: boolean;
  [MediaKeys.INTPUT_BOARDS_VIDEO_ENABLED]: boolean;
}

export const setMediaParam = <T extends MediaKeys>(
  key: T,
  data: MediaData[T]
) => setItem(StorageCategory.MEDIA, key, JSON.stringify(data));

export const getMediaParam = <T extends MediaKeys>(key: T): MediaData[T] | null => {
  const str = getItem(StorageCategory.MEDIA, key);
  if (!str) return null;
  let result = null;
  try {
    result = JSON.parse(str);
  } catch(err) {}
  return result;
};

export const removeMediaParam = <T extends MediaKeys>(key: T) => removeItem(StorageCategory.MEDIA, key);

const Storage = {
  // TOKEN
  setToken,
  getToken,
  removeToken,
  // REFRESH TOKEN
  // SETTINGS
  setSettings,
  getSettings,
  removeSettings,
  // EXPERT
  setExpertHomeParams,
  getExpertHomeParams,
  removeExpertHomeParams,
  // STUDENT
  setStudentParam,
  getStudentParam,
  removeStudentParam,
  // MEDIA
  setMediaParam,
  getMediaParam,
  removeMediaParam,
};
export default Storage;
